/**
 * @fileoverview OTP verification form component
 * @description Handles email verification with 6-digit OTP code input
 * @author Taqat AI Team
 * @since 2024-12-09
 */

import * as React from "react"
import { useForm } from "react-hook-form"
import { safeZodResolver, onFormValidationError } from "@/utils/safe-zod-resolver"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { FormItem, FormMessage } from "@/components/ui/form"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui"
import { authService } from "@/services/auth-service"
import { useAuthActions } from "@/store"
import { workspaceStore } from "@/store/slices/workspace-slice"
import type { User } from "@/lib/auth-provider"
import { useNavigate, useSearchParams } from "react-router-dom"
import { withInvitationId } from "@/utils/invitation-url"

interface VerificationResponse {
  success: boolean
  data?: {
    session?: {
      sessionToken: string
      refreshToken?: string
      expires: string
    }
    user: User
    workspaces?: any[]
    selectedWorkspace?: any
  }
  error?: {
    message: string
  }
}

interface ResendResponse {
  success: boolean
  message: string
  error?: {
    message: string
  }
}

/**
 * Configuration constants
 */
const OTP_CONFIG = {
  LENGTH: 6,
  RESEND_COOLDOWN: 60,
  INPUT_PATTERN: /^\d{6}$/,
} as const

/**
 * Validation schema for OTP verification form
 */
const verifyOtpSchema = z.object({
  otp: z
    .string()
    .min(1, "Verification code is required")
    .length(OTP_CONFIG.LENGTH, `Verification code must be ${OTP_CONFIG.LENGTH} digits`)
    .regex(OTP_CONFIG.INPUT_PATTERN, `Verification code must be ${OTP_CONFIG.LENGTH} digits`),
})

/**
 * Form data type for OTP verification
 */
type VerifyOtpFormData = z.infer<typeof verifyOtpSchema>


/**
 * Props for VerifyOtpForm component
 */
export interface VerifyOtpFormProps {
  /** OTP verification ID from signup process */
  otpId?: string | null
  /** Callback executed on successful verification */
  onSuccess?: () => void
  /** Callback to navigate back to signup */
  onBackToSignup?: () => void
}

/**
 * OTP verification form component
 * Handles email verification with 6-digit OTP code
 * Features auto-formatting, resend functionality, and proper error handling
 */
export function VerifyOtpForm({ otpId, onSuccess, onBackToSignup }: VerifyOtpFormProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const invitationId = searchParams.get('invitationId')

  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [isResending, setIsResending] = React.useState(false)
  const [resendMessage, setResendMessage] = React.useState<string | null>(null)
  const [countdown, setCountdown] = React.useState(0)
  const [otpValue, setOtpValue] = React.useState("")
  const { setUser, setLoading: setAuthLoading } = useAuthActions()

  const form = useForm<VerifyOtpFormData>({
    resolver: safeZodResolver(verifyOtpSchema as any) as any,
    defaultValues: {
      otp: "",
    },
  })

  /**
   * Countdown timer effect for resend button cooldown
   */
  React.useEffect(() => {
    let timer: NodeJS.Timeout
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(prev => prev - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [countdown])

  /**
   * Handle OTP value change
   * @param value - New OTP value
   */
  const handleOtpChange = React.useCallback((value: string) => {
    setOtpValue(value)
    form.setValue('otp', value)
    if (error) setError(null) // Clear error on input change
  }, [form, error])

  /**
   * Guards the verification request against being sent more than once for the
   * same OTP session.
   *
   * A code can only be redeemed once: the second POST comes back 400 "This
   * verification code has already been used", which used to be reported as a
   * failed verification even though the first request had already signed the
   * user in. Re-entry is easy to hit here because the auto-submit effect below
   * re-runs whenever `onSubmit` changes identity, and a successful
   * verification does exactly that — it writes to the auth store, which
   * re-renders the page that owns this form's callbacks, before the navigation
   * unmounts the form.
   *
   * A ref (not state) because the guard has to be readable and writable
   * synchronously, in the same tick the duplicate submit would start.
   */
  const verifyGuardRef = React.useRef({ inFlight: false, verified: false })

  /**
   * Handles OTP verification form submission
   * @param data - Form data containing OTP code
   */
  const onSubmit = React.useCallback(async (data: VerifyOtpFormData) => {
    if (!otpId) {
      setError("Invalid verification session. Please restart the signup process.")
      return
    }

    // Single funnel for every submit path — the auto-submit effect, the button
    // and Enter — so none of them can double-redeem the code.
    const guard = verifyGuardRef.current
    if (guard.inFlight || guard.verified) {
      return
    }
    guard.inFlight = true

    setIsLoading(true)
    setError(null)
    setResendMessage(null)

    try {
      const response = await authService.verifyOtp({
        otpId,
        otp: data.otp
      }) as VerificationResponse

      if (response.success && response.data) {
        // Code redeemed. Latch it so a re-render can't replay the submit while
        // the navigation below is still settling.
        guard.verified = true

        // Update auth state with verified user
        setAuthLoading(true)
        setUser(response.data.user)

        // If coming from an invitation flow, redirect to /app with invitation params
        // The InvitationHandler component will accept the invitation and switch workspace
        if (invitationId) {
          navigate(withInvitationId('/app', invitationId), { replace: true })
          return
        }

        // Check if user has workspaces
        if (response.data.workspaces && response.data.workspaces.length > 0) {
          // Update workspace store with the workspaces
          const workspaceActions = workspaceStore.getState()
          workspaceActions.setWorkspaces(response.data.workspaces)
          if (response.data.selectedWorkspace) {
            workspaceActions.setCurrentWorkspace(response.data.selectedWorkspace)
          }
          // User has workspaces, navigate to home page
          navigate(`/app/dashboard`, { replace: true });
        } else {
          // User has no workspaces, navigate to workspace creation
          navigate(`/workspace/create`, { replace: true });
        }
      } else {
        const errorMessage = response.error?.message || "Verification failed"
        setError(errorMessage)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Verification failed"
      setError(errorMessage)
    } finally {
      guard.inFlight = false
      setIsLoading(false)
      setAuthLoading(false)
    }
    // `onSuccess` is deliberately not a dependency: it is not read here, and
    // callers pass an inline arrow, so including it made this callback — and
    // therefore the auto-submit effect below — churn on every parent render.
  }, [otpId, setAuthLoading, setUser, invitationId, navigate])

  /**
   * Submit form when OTP is complete
   */
  React.useEffect(() => {
    if (otpValue.length === OTP_CONFIG.LENGTH) {
      form.handleSubmit(onSubmit, onFormValidationError)()
    }
  }, [otpValue, form, onSubmit])

  /**
   * Handles resending OTP verification code
   */
  const handleResendOtp = React.useCallback(async () => {
    if (!otpId || countdown > 0 || isResending) return

    setIsResending(true)
    setResendMessage(null)
    setError(null)

    try {
      const response = await authService.resendOtp({ otpId }) as ResendResponse

      if (response.success) {
        // A new code was issued, so the previous one being spent is irrelevant.
        verifyGuardRef.current.verified = false
        setResendMessage("Verification code sent successfully!")
        setCountdown(OTP_CONFIG.RESEND_COOLDOWN)
        // Clear form to encourage entering new code
        setOtpValue('')
        form.reset({ otp: '' })
      } else {
        const errorMessage = response.error?.message || "Failed to resend verification code"
        setError(errorMessage)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to resend verification code"
      setError(errorMessage)
    } finally {
      setIsResending(false)
    }
  }, [otpId, countdown, isResending, form])

  return (
    <div className="w-full">
      <div className="text-left mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          Verify Your Email
        </h1>
        <p className="text-sm text-muted-foreground">
          Enter the 6-digit code sent to your email address
        </p>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit, onFormValidationError)} className="space-y-4">
        <FormItem>
          <div className="flex justify-center">
            <InputOTP
              maxLength={OTP_CONFIG.LENGTH}
              value={otpValue}
              onChange={handleOtpChange}
              disabled={isLoading}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          <FormMessage className="text-center mt-2">
            {form.formState.errors.otp?.message}
          </FormMessage>
        </FormItem>

        {error && (
          <div className="rounded-md bg-red-50 p-3 border border-red-200">
            <div className="text-sm text-red-800">
              {error}
            </div>
          </div>
        )}

        {resendMessage && (
          <div className="rounded-md bg-green-50 p-3 border border-green-200">
            <div className="text-sm text-green-800 text-center">
              {resendMessage}
            </div>
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading || !otpId}
        >
          {isLoading ? "Verifying..." : "Verify Email"}
        </Button>

        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Didn't receive the code?
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleResendOtp}
            disabled={isResending || countdown > 0 || !otpId || isLoading}
            className="text-primary hover:text-primary/80 disabled:opacity-50"
            aria-label={countdown > 0 ? `Resend code available in ${countdown} seconds` : "Resend verification code"}
          >
            {isResending ? "Sending..." : countdown > 0 ? `Resend in ${countdown}s` : "Resend Code"}
          </Button>
        </div>

        <div className="text-center text-sm mt-6">
          <p className="text-muted-foreground">
            Need to use a different email?{" "}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onBackToSignup}
              className="text-primary hover:text-primary/80 underline-offset-4 hover:underline p-0 h-auto"
            >
              Back to signup
            </Button>
          </p>
        </div>
      </form>
    </div>
  )
}
